import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ClimateMap from '../ClimateMap';
import SourceInfoCard from './SourceInfoCard';
import VariableAssignmentTable, { CLIMATE_VARIABLES } from './VariableAssignmentTable';
import YearRangeSelector from './YearRangeSelector';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const LocationCreateWizard = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1);

  // Step 1: coordinates
  const [pickedLat, setPickedLat] = useState(null);
  const [pickedLon, setPickedLon] = useState(null);

  // Step 2: details
  const [formData, setFormData] = useState({
    location_code: '', name: '', description: '',
    elevation_m: '', timezone: 'Africa/Johannesburg', country: 'South Africa', region: '',
  });

  // Step 3: sources & variables
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [variableAssignments, setVariableAssignments] = useState({});
  const [startYear, setStartYear] = useState(2005);
  const [endYear, setEndYear] = useState(2023);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState('');

  // Fetch sources when coordinates change
  useEffect(() => {
    if (!pickedLat || !pickedLon) return;
    setSourcesLoading(true);
    fetch(`${API}/api/sources/availability?lat=${pickedLat}&lon=${pickedLon}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.sources) {
          setSources(data.sources.filter(s => s.integrated));
        }
      })
      .catch(() => {})
      .finally(() => setSourcesLoading(false));
  }, [pickedLat, pickedLon]);

  // Selected source IDs
  const selectedSourceIds = useMemo(
    () => [...new Set(Object.values(variableAssignments))],
    [variableAssignments]
  );

  // Year bounds from selected sources
  const yearBounds = useMemo(() => {
    const used = sources.filter(s => selectedSourceIds.includes(s.id));
    if (used.length === 0) return { min: 2005, max: 2024 };
    return {
      min: Math.max(...used.map(s => s.year_min || 2005)),
      max: Math.min(...used.map(s => s.year_max || 2024)),
    };
  }, [selectedSourceIds, sources]);

  useEffect(() => {
    setStartYear(prev => Math.max(prev, yearBounds.min));
    setEndYear(prev => Math.min(prev, yearBounds.max));
  }, [yearBounds]);

  // Validation
  const step2Valid = formData.location_code.length === 5 && formData.name.trim() && formData.description.trim() && pickedLat && pickedLon;
  const step3Valid = Object.keys(variableAssignments).length >= 1 && startYear <= endYear;
  const canDownload = step2Valid && step3Valid;

  const handleMapClick = (lat, lng) => {
    if (step !== 1) return;
    const parsedLat = parseFloat(lat.toFixed(6));
    const parsedLon = parseFloat(lng.toFixed(6));
    setPickedLat(parsedLat);
    setPickedLon(parsedLon);
    fetch(`${API}/api/elevation?lat=${parsedLat}&lon=${parsedLon}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.elevation_m != null) {
          setFormData(prev => ({ ...prev, elevation_m: String(Math.round(d.elevation_m)) }));
        }
      })
      .catch(() => {});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'location_code') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase().slice(0, 5) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSourceToggle = useCallback((src) => {
    setVariableAssignments(prev => {
      const next = { ...prev };
      const isUsed = Object.values(prev).includes(src.id);
      if (isUsed) {
        for (const key of Object.keys(next)) { if (next[key] === src.id) delete next[key]; }
      } else {
        for (const v of CLIMATE_VARIABLES) {
          if ((src.variables || []).includes(v.id) && !next[v.id]) next[v.id] = src.id;
        }
      }
      return next;
    });
  }, []);

  const handleAssignVariable = (varId, sourceId) => {
    setVariableAssignments(prev => {
      const next = { ...prev };
      if (sourceId) { next[varId] = sourceId; } else { delete next[varId]; }
      return next;
    });
  };

  // Parse a response defensively: a proxy/HTML error page must surface as a
  // readable message, not "Unexpected token '<' ... is not valid JSON".
  const readJson = async (res) => {
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      const snippet = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
      throw new Error(
        `Server returned ${res.status} ${res.statusText || ''} (non-JSON response)` +
        (snippet ? `: ${snippet}` : '')
      );
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setMessage('Saving location & downloading... this may take 30\u201390 seconds per source.');
    try {
      const locPayload = {
        location_code: formData.location_code,
        name: formData.name,
        description: formData.description,
        latitude: pickedLat, longitude: pickedLon,
        elevation_m: formData.elevation_m ? parseFloat(formData.elevation_m) : null,
        timezone: formData.timezone, country: formData.country, region: formData.region,
      };
      const locRes = await fetch(`${API}/api/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(locPayload),
      });
      const savedLoc = await readJson(locRes);
      if (!locRes.ok) {
        throw new Error(savedLoc.error || 'Failed to save location');
      }

      const dlRes = await fetch(`${API}/api/locations/${savedLoc.id}/download/mixed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variable_sources: variableAssignments, startyear: startYear, endyear: endYear }),
      });
      const dlJson = await readJson(dlRes);
      if (dlRes.ok) {
        onComplete(savedLoc.id);
      } else {
        setMessage(`Error: ${dlJson.error}`);
      }
    } catch (e) {
      setMessage(`Error: ${e.message}`);
    }
    setDownloading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Step indicator */}
      <div className="wizard-banner">
        <div className="wizard-steps">
          <span className={`wizard-step-dot ${step >= 1 ? 'active' : ''} ${step === 1 ? 'current' : ''}`}>1</span>
          <span className="wizard-step-line" />
          <span className={`wizard-step-dot ${step >= 2 ? 'active' : ''} ${step === 2 ? 'current' : ''}`}>2</span>
          <span className="wizard-step-line" />
          <span className={`wizard-step-dot ${step >= 3 ? 'active' : ''} ${step === 3 ? 'current' : ''}`}>3</span>
        </div>
        <div className="wizard-step-label">
          {step === 1 && 'Step 1: Pick location on map'}
          {step === 2 && 'Step 2: Location details'}
          {step === 3 && 'Step 3: Select data sources & download'}
        </div>
        <button className="btn-small btn-cancel" onClick={onCancel}>Cancel</button>
      </div>

      {message && (
        <div className={`download-msg ${message.startsWith('Error') ? 'error' : 'success'}`}>
          {message}
          <button className="btn-icon" onClick={() => setMessage('')}>&times;</button>
        </div>
      )}

      {/* Step 1: Map pick */}
      {step === 1 && (
        <div className="climate-map-area">
          <ClimateMap
            location={pickedLat ? { latitude: pickedLat, longitude: pickedLon } : { latitude: -28.5, longitude: 25.5 }}
            sources={[]}
            onMapClick={handleMapClick}
            pickMode={true}
          />
          <div className="pick-panel">
            <div className="pick-panel-title">Click on the map to place your location</div>
            {pickedLat ? (
              <>
                <div className="pick-coords">
                  <span className="pick-coord-label">Lat:</span>
                  <span className="pick-coord-value">{pickedLat}</span>
                  <span className="pick-coord-label">Lon:</span>
                  <span className="pick-coord-value">{pickedLon}</span>
                  {formData.elevation_m && (
                    <>
                      <span className="pick-coord-label">Elev:</span>
                      <span className="pick-coord-value">{formData.elevation_m}m</span>
                    </>
                  )}
                </div>
                <button className="btn-small btn-green" onClick={() => setStep(2)}>
                  Confirm Location &rarr;
                </button>
              </>
            ) : (
              <div className="pick-hint">Zoom in to find your site, then click to place a marker</div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Details form */}
      {step === 2 && (
        <div className="wizard-form-area">
          <div className="wizard-form-split">
            <div className="wizard-form-map">
              <ClimateMap
                location={{ latitude: pickedLat, longitude: pickedLon }}
                sources={[]}
                onMapClick={handleMapClick}
                pickMode={true}
              />
            </div>
            <div className="wizard-form-fields">
              <h3>Location Details</h3>
              <div className="form-group">
                <label><span className={formData.location_code.length === 5 ? 'valid-star' : 'required-star'}>*</span> Location Code (5 chars)</label>
                <input name="location_code" value={formData.location_code} onChange={handleFormChange}
                  placeholder="PPR01" maxLength={5}
                  className={formData.location_code.length === 5 ? 'field-valid' : formData.location_code ? 'field-invalid' : ''} />
              </div>
              <div className="form-group">
                <label><span className={formData.name.trim() ? 'valid-star' : 'required-star'}>*</span> Name</label>
                <input name="name" value={formData.name} onChange={handleFormChange} placeholder="e.g. PPR Chemical Plant"
                  className={formData.name.trim() ? 'field-valid' : ''} />
              </div>
              <div className="form-group">
                <label><span className={formData.description.trim() ? 'valid-star' : 'required-star'}>*</span> Description</label>
                <input name="description" value={formData.description} onChange={handleFormChange}
                  placeholder="e.g. Prieska Cluster - main site"
                  className={formData.description.trim() ? 'field-valid' : ''} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input type="number" step="any" value={pickedLat || ''} readOnly className="field-valid field-readonly-ish" />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input type="number" step="any" value={pickedLon || ''} readOnly className="field-valid field-readonly-ish" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Elevation (m)</label>
                  <input name="elevation_m" type="number" value={formData.elevation_m} onChange={handleFormChange} placeholder="980" />
                </div>
                <div className="form-group">
                  <label>Timezone</label>
                  <input name="timezone" value={formData.timezone} onChange={handleFormChange} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Country</label>
                  <input name="country" value={formData.country} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label>Region</label>
                  <input name="region" value={formData.region} onChange={handleFormChange} placeholder="Northern Cape" />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setStep(1)}>&larr; Back</button>
                <button type="button" className="btn-primary" disabled={!step2Valid} onClick={() => setStep(3)}>
                  Next: Select Data &rarr;
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Source selection & download */}
      {step === 3 && (
        <div className="wizard-sources-area">
          <div className="wizard-sources-split">
            <div className="wizard-sources-map">
              <ClimateMap
                location={{ latitude: pickedLat, longitude: pickedLon, name: formData.name }}
                sources={sources}
                onMapClick={() => {}}
                onSourceSelect={handleSourceToggle}
                pickMode={false}
                selectedSources={selectedSourceIds}
              />
            </div>
            <div className="wizard-sources-panel">
              <h3>Climate Data Sources</h3>
              <p className="form-hint">
                Select data sources and assign variables. Sources are probed for live availability.
                {sourcesLoading && <span style={{ color: '#3b82f6' }}> Loading sources...</span>}
              </p>

              {/* Source info cards */}
              <div className="source-cards">
                {sources.map(src => (
                  <SourceInfoCard
                    key={src.id}
                    source={src}
                    selected={selectedSourceIds.includes(src.id)}
                    onClick={() => handleSourceToggle(src)}
                  />
                ))}
              </div>

              {/* Variable assignment */}
              <div className="var-section-title">Variable Assignment</div>
              <VariableAssignmentTable
                sources={sources}
                assignments={variableAssignments}
                onAssign={handleAssignVariable}
              />

              {/* Year range */}
              <div className="var-section-title" style={{ marginTop: 16 }}>Download Period</div>
              <YearRangeSelector
                startYear={startYear}
                endYear={endYear}
                onStartChange={setStartYear}
                onEndChange={setEndYear}
                availableMin={yearBounds.min}
                availableMax={yearBounds.max}
              />

              {/* Summary */}
              <div className="var-summary" style={{ marginTop: 16 }}>
                <div className="var-summary-title">DOWNLOAD SUMMARY</div>
                <div className="var-summary-line">
                  <span>Location:</span>
                  <span>{formData.location_code} &mdash; {formData.name}</span>
                </div>
                <div className="var-summary-line">
                  <span>GPS:</span>
                  <span>{pickedLat}, {pickedLon}</span>
                </div>
                <div className="var-summary-line">
                  <span>Period:</span>
                  <span>{startYear}&ndash;{endYear}</span>
                </div>
                <div className="var-summary-line">
                  <span>Variables:</span>
                  <span>{Object.keys(variableAssignments).length > 0
                    ? Object.keys(variableAssignments).join(', ')
                    : <span className="required-star">None selected</span>}
                  </span>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setStep(2)}>&larr; Back</button>
                <button
                  type="button" className="btn-primary btn-download"
                  disabled={!canDownload || downloading}
                  onClick={handleDownload}
                >
                  {downloading ? 'Downloading...' : 'Download Climate Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationCreateWizard;
